<script setup>
const showCountrySelector = ref(false);
import '/node_modules/flag-icons/css/flag-icons.min.css';

import { computed, ref, watch } from 'vue';

import LoadingSpinner from '@/shared/components/UI/LoadingSpinner.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useTranslations } from '@/shared/composables/useTranslations.ts';
import { storeCountryCode } from '@/shared/utils/country';
import { resolvePreservePathByStore } from '@/shared/utils/country-switch-targets';
import { buildCountrySwitchUrl } from '@/shared/utils/country-switch-url';

import IconChevron from './Icons/IconChevron.vue';

const { t } = useTranslations();

const props = defineProps({
  countryConfigs: {
    type: Array,
    required: true
  },
  cartCurrency: {
    type: String,
    required: true
  }
});
const currentCountryCode = storeCountryCode().toLowerCase();

const preservePathByStore = ref(new Map());
const isResolvingCountries = ref(false);
const hasResolvedCountries = ref(false);

// Resolved once per page load, not once per open: collection filters and
// variant pickers only rewrite the query string via replaceState, never the
// pathname, so a resolution done on the first open can't go stale later on
// the same page.
watch(showCountrySelector, async (isOpen) => {
  if (!isOpen || hasResolvedCountries.value || isResolvingCountries.value) {
    return;
  }

  isResolvingCountries.value = true;

  try {
    preservePathByStore.value = await resolvePreservePathByStore(
      props.countryConfigs
    );
  } catch (error) {
    // The list is gated on hasResolvedCountries, so a failure here must still
    // settle both flags or the modal spins forever with no way to switch
    // country. An empty map means every link preserves the path, which is the
    // behaviour this component had before the check existed.
    //
    // The reporter is loaded dynamically rather than imported statically:
    // this component mounts in the footer on every page, and a static import
    // pulled Bugsnag into that chunk, flipping the bundler's chunking so the
    // shared notifier chunk got inlined here and `common` grew a hard
    // dependency on it. Deferring the fetch to only-when-needed is also
    // simply correct - resolution failures are rare.
    try {
      const { notifyBugsnagWithContext } = await import('@/shared/utils/error');
      notifyBugsnagWithContext(
        'CountrySwitcher.resolvePreservePathByStore',
        error
      );
    } catch {
      // Reporting must not become a second failure path.
    }
  } finally {
    hasResolvedCountries.value = true;
    isResolvingCountries.value = false;
  }
});

const countries = computed(() => {
  const mapped = props.countryConfigs
    .filter((countryConfig) => countryConfig.shopifyConfig?.storeEndpoint)
    .map((countryConfig) => ({
      code: countryConfig.countryCode,
      name: t(`global.countries.${countryConfig.countryCode.toLowerCase()}`),
      storeEndpoint: countryConfig.shopifyConfig.storeEndpoint
    }));

  mapped.sort((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });

  return mapped;
});

// Built per render rather than cached in the computed above: variant pickers
// and collection filters rewrite the URL in place via replaceState, so a
// cached href would carry a stale query string to the target store.
const getCountrySwitcherUrl = (country) =>
  buildCountrySwitchUrl(country.storeEndpoint, country.code, {
    // Fail-open: a store missing from the resolved map (e.g. resolution
    // hasn't run yet) keeps today's behaviour of always preserving the path.
    preservePath: preservePathByStore.value.get(country.storeEndpoint) ?? true
  });

const getFlagClass = (country) =>
  country ? `fi fi-${country.toLowerCase()}` : '';
</script>

<template>
  <button
    class="flex items-center gap-1.5 rounded-md border border-transparent py-2 text-white hover:text-primary-200"
    @click="showCountrySelector = !showCountrySelector"
  >
    <span
      :class="getFlagClass(currentCountryCode)"
      class="shrink-0 rounded text-lg"
    ></span>
    <span class="font-bold underline"
      >{{ t(`global.countries.${currentCountryCode}`) }} ({{
        cartCurrency
      }})</span
    >
    <IconChevron
      :direction="showCountrySelector ? 'up' : 'down'"
      class="-ml-1 text-xl"
    />
  </button>

  <Modal v-model:active="showCountrySelector" size="lg">
    <template #header>
      <h2 class="flex w-full justify-center text-xl font-bold">
        {{ t('footer.country_switcher.change_country') }}
      </h2>
    </template>
    <template #body>
      <div class="relative flex min-h-[120px] flex-col">
        <LoadingSpinner :show="isResolvingCountries" />

        <div
          v-if="hasResolvedCountries"
          class="columns-2 space-y-1 md:columns-3"
        >
          <template v-for="country in countries" :key="country.countryCode">
            <a
              :href="getCountrySwitcherUrl(country)"
              class="flex items-center gap-2 rounded-md p-2 text-sm transition-colors duration-100 hover:bg-gray-100 hover:text-secondary hover:no-underline"
            >
              <span
                :class="getFlagClass(country.code)"
                class="shrink-0 rounded text-lg"
                :title="country.name"
              ></span>

              <span>{{ country.name }}</span>
            </a>
          </template>
        </div>
      </div>
    </template>
  </Modal>
</template>
